import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { performHttpFetch } from '../../common/utils/fetch';
import { isClientFetchError } from '../../common/utils/is-error';
import { getRovoChatProduct } from '../../common/utils/product-config';
import { normaliseUrl } from './utils';

// importing local file instead of from '@sentry/browser' otherwise there will be typescript errors
var importSentryAsync = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          return _context.abrupt("return", import( /* webpackChunkName: "@atlaskit-internal_conversation-assistant-sentry" */
          '@sentry/browser'));
        case 1:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function importSentryAsync() {
    return _ref.apply(this, arguments);
  };
}();
var sentryClient;

// copied from platform/packages/people-and-teams/teams-client/src/services/sentry/utils/initialise-sentry.ts
var DISABLED_INTEGRATIONS = ['onerror', 'onunhandledrejection',
// In Jira this integration is being initialised twice, resulting in no events being sent to Sentry
'Dedupe'];
var getEnvironment = function getEnvironment() {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  if (process.env.CLOUD_ENV === 'staging') {
    return 'staging';
  }
  return 'production';
};
export var setupSentryClient = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var _ref3,
      customSentryDsn,
      packageName,
      productOverride,
      accountId,
      product,
      profileData,
      importedSentry,
      defaultSentryDsn,
      _args2 = arguments;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _ref3 = _args2.length > 0 && _args2[0] !== undefined ? _args2[0] : {}, customSentryDsn = _ref3.customSentryDsn, packageName = _ref3.packageName, productOverride = _ref3.product;
          accountId = 'UNKNOWN_AAID';
          product = productOverride !== null && productOverride !== void 0 ? productOverride : getRovoChatProduct();
          _context2.prev = 3;
          _context2.next = 6;
          return performHttpFetch({
            path: '/gateway/api/me',
            options: {
              method: 'GET'
            }
          });
        case 6:
          profileData = _context2.sent;
          if (profileData !== null && profileData !== void 0 && profileData.account_id) {
            accountId = profileData.account_id;
          }
          _context2.next = 12;
          break;
        case 10:
          _context2.prev = 10;
          _context2.t0 = _context2["catch"](3);
        case 12:
          if (!sentryClient) {
            _context2.next = 16;
            break;
          }
          // if sentry is trying to be initialized again with new product or account id
          // update the user and tags
          sentryClient.setTag('product', product);
          sentryClient.setUser({
            id: accountId
          });
          // do not initialise sentry again, only update the existing one
          return _context2.abrupt("return");
        case 16:
          _context2.prev = 16;
          _context2.next = 19;
          return importSentryAsync();
        case 19:
          importedSentry = _context2.sent;
          _context2.next = 26;
          break;
        case 22:
          _context2.prev = 22;
          _context2.t1 = _context2["catch"](16);
          console.warn('Failed to load conversation-assistant sentry', _context2.t1); // eslint-disable-line no-console
          return _context2.abrupt("return");
        case 26:
          // https://atlassian-2y.sentry.io/projects/ai-mate-conversation-assistant/getting-started/
          defaultSentryDsn = 'https://0d679a1c0fe3816e2b2e34d4ca85d5da@o55978.ingest.sentry.io/4506794008903680';
          importedSentry.init({
            dsn: customSentryDsn || defaultSentryDsn,
            environment: getEnvironment(),
            integrations: function integrations(_integrations) {
              return _integrations.filter(function (integration) {
                return DISABLED_INTEGRATIONS.indexOf(integration.name) === -1;
              });
            },
            initialScope: {
              user: {
                id: accountId
              },
              tags: {
                packageName: packageName || '@atlassian/conversation-assistant',
                product: product
              }
            },
            // referencing jira/src/packages/platform/observability/errors-handling/src/utils/sentry/handling/normalise-sentry-event.tsx
            beforeSend: function beforeSend(event, hint) {
              var _event$tags, _updatedEvent$request;
              var exception = hint === null || hint === void 0 ? void 0 : hint.originalException;
              // Ignore network-like errors
              if (isClientFetchError(exception)) {
                return null;
              }
              if (!((_event$tags = event.tags) !== null && _event$tags !== void 0 && _event$tags.name)) {
                // we only want to capture errors that has `name` tag from `logException` function below
                // since the `DISABLED_INTEGRATIONS` filter above is not enough to prevent unhandled errors
                return null;
              }
              var updatedEvent = event;
              if ((_updatedEvent$request = updatedEvent.request) !== null && _updatedEvent$request !== void 0 && _updatedEvent$request.url) {
                // Ignore events from localhost and staging
                if (updatedEvent.request.url.includes('localhost') || updatedEvent.request.url.includes('jira-dev.com') || updatedEvent.request.url.includes('stg.atlassian.com') || updatedEvent.request.url.includes('stg.internal.atlassian.com') || updatedEvent.request.url.includes('stg-east.frontend.public.atl-paas.net')) {
                  return null;
                }
                updatedEvent.request.url = normaliseUrl(updatedEvent.request.url);
              }
              return updatedEvent;
            },
            attachStacktrace: true
          });
          sentryClient = importedSentry;
        case 29:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[3, 10], [16, 22]]);
  }));
  return function setupSentryClient() {
    return _ref2.apply(this, arguments);
  };
}();
var getSentryClient = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          if (sentryClient) {
            _context3.next = 3;
            break;
          }
          _context3.next = 3;
          return setupSentryClient();
        case 3:
          return _context3.abrupt("return", sentryClient);
        case 4:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return function getSentryClient() {
    return _ref4.apply(this, arguments);
  };
}();
export var logException = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(_ref5) {
    var exception, name, tags, sentryClient;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) switch (_context4.prev = _context4.next) {
        case 0:
          exception = _ref5.exception, name = _ref5.name, tags = _ref5.tags;
          _context4.next = 3;
          return getSentryClient();
        case 3:
          sentryClient = _context4.sent;
          if (!(!sentryClient || typeof sentryClient.captureException !== 'function')) {
            _context4.next = 6;
            break;
          }
          return _context4.abrupt("return");
        case 6:
          sentryClient.withScope(function (scope) {
            scope.setTag('name', name);
            if (tags) {
              scope.setTags(tags);
            }
            sentryClient.captureException(exception, scope);
          });
        case 7:
        case "end":
          return _context4.stop();
      }
    }, _callee4);
  }));
  return function logException(_x) {
    return _ref6.apply(this, arguments);
  };
}();